import React, { useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PORTFOLIO_PROJECTS } from '../../data/portfolioData';
import { PortfolioHeroStats } from './PortfolioHeroStats';
import { FeaturedProjects } from './FeaturedProjects';
import { ProjectCard } from './ProjectCard';
import { ProjectModal } from './ProjectModal';

// Show 3 non-featured preview projects on the homepage
const HOMEPAGE_PREVIEW_COUNT = 3;

// Varying masonry heights for the preview grid
const HEIGHTS = ['aspect-[4/3]', 'aspect-[3/2]', 'aspect-square'];

const PortfolioSection = () => {
  const [selectedProject, setSelectedProject] = useState(null);

  const featured = PORTFOLIO_PROJECTS.filter((p) => p.featured);
  const previewGrid = PORTFOLIO_PROJECTS.filter((p) => !p.featured).slice(0, HOMEPAGE_PREVIEW_COUNT);

  return (
    <section id="portfolio" className="bg-[#0A0A0A]" data-testid="portfolio-section">
      {/* Header + Stats */}
      <PortfolioHeroStats />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {/* Featured Projects */}
        <div className="mb-10">
          <p className="text-[#6B7280] text-[10px] uppercase tracking-widest font-body mb-4">Featured Projects</p>
          <FeaturedProjects projects={featured} onOpenModal={setSelectedProject} />
        </div>

        {/* Preview Masonry Grid (3 more projects) */}
        <div className="mb-8">
          <p className="text-[#6B7280] text-[10px] uppercase tracking-widest font-body mb-4">More Projects</p>
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-px" data-testid="portfolio-grid">
            {previewGrid.map((project, i) => (
              <div key={project.id} className="break-inside-avoid mb-px">
                <ProjectCard
                  project={project}
                  onClick={setSelectedProject}
                  heightClass={HEIGHTS[i % HEIGHTS.length]}
                />
              </div>
            ))}
          </div>
        </div>

        {/* See All Projects Button */}
        <div className="flex justify-center mb-10" data-testid="see-all-projects">
          <Link
            to="/portfolio"
            className="flex items-center gap-3 border border-[#4A4A4A] text-[#D1D5DB] text-xs font-heading uppercase tracking-[0.2em] px-8 py-4 hover:border-[#C41E3A] hover:text-white transition-colors group"
            data-testid="see-all-btn"
          >
            View All Projects
            <ArrowRight size={14} strokeWidth={2} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* CTA Strip */}
        <div className="border border-[#4A4A4A] bg-[#0D0D0D] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="portfolio-cta">
          <div className="w-1 h-10 bg-[#C41E3A] shrink-0 hidden sm:block" />
          <div className="flex-1">
            <p className="font-heading text-xl text-white">NEED A SIMILAR INDUSTRIAL FACILITY?</p>
            <p className="text-[#9CA3AF] text-sm font-body mt-0.5">
              From industrial sheds and warehouses to large-scale PEB manufacturing complexes — let's build yours.
            </p>
          </div>
          <a
            href="#contact"
            data-testid="portfolio-cta-btn"
            className="shrink-0 flex items-center gap-2 bg-[#C41E3A] text-white text-xs font-bold uppercase tracking-[0.2em] px-7 py-4 clip-btn hover:bg-white hover:text-[#0A0A0A] transition-colors whitespace-nowrap"
          >
            Get In Touch <ArrowRight size={14} strokeWidth={2} />
          </a>
        </div>
      </div>

      {/* Modal */}
      {selectedProject && (
        <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      )}
    </section>
  );
};

export default PortfolioSection;
